import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useApi } from '../../contexts/ApiContext'
import { supabase } from '../../lib/supabase'
import { User, Building2, Eye, EyeOff, Loader, ArrowLeft, Shield } from 'lucide-react'
import toast from 'react-hot-toast'
import { trackLoginPageView, trackLoginAttempt, trackLoginSuccess, trackLoginFailure } from '../../utils/eventTracking'
import { identifyOneSignalUser } from '../../config/onesignal'

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [userType, setUserType] = useState('customer')
  
  const { login } = useAuth()
  const { getCustomers } = useApi()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const type = searchParams.get('type')
    if (type === 'employee') {
      setUserType('employee')
    } else if (type === 'customer') {
      setUserType('customer')
    }
    trackLoginPageView({ user_type: type || 'customer' })
  }, [searchParams])

  // Function to get user's IP address and location
  const getUserLocation = async () => {
    try {
      // Try to get IP from a free service
      const ipResponse = await fetch('https://api.ipify.org?format=json')
      const ipData = await ipResponse.json()
      const userIP = ipData.ip
      
      // Try to get location from IP
      try {
        const locationResponse = await fetch(`https://ipapi.co/${userIP}/json/`)
        const locationData = await locationResponse.json()
        
        return {
          ip: userIP,
          city: locationData.city || 'Mumbai',
          state: locationData.region || 'Maharashtra', 
          country: locationData.country_name || 'India',
          latitude: locationData.latitude || 19.0760,
          longitude: locationData.longitude || 72.8777
        }
      } catch (locationError) {
        console.warn('Location lookup failed, using defaults:', locationError)
        return {
          ip: userIP,
          city: 'Mumbai',
          state: 'Maharashtra',
          country: 'India',
          latitude: 19.0760,
          longitude: 72.8777
        }
      }
    } catch (ipError) {
      console.warn('IP lookup failed, using defaults:', ipError)
      return {
        ip: '127.0.0.1',
        city: 'Mumbai',
        state: 'Maharashtra',
        country: 'India',
        latitude: 19.0760,
        longitude: 72.8777
      }
    }
  }

  // Function to detect browser and OS
  const getBrowserInfo = () => {
    const userAgent = navigator.userAgent
    let browser = 'Unknown'
    let os = 'Unknown'
    
    // Detect browser
    if (userAgent.includes('Chrome')) browser = 'Chrome'
    else if (userAgent.includes('Firefox')) browser = 'Firefox'
    else if (userAgent.includes('Safari')) browser = 'Safari'
    else if (userAgent.includes('Edge')) browser = 'Edge'
    
    // Detect OS
    if (userAgent.includes('Windows')) os = 'Windows'
    else if (userAgent.includes('Mac')) os = 'MacOS'
    else if (userAgent.includes('Linux')) os = 'Linux'
    else if (userAgent.includes('Android')) os = 'Android'
    else if (userAgent.includes('iOS')) os = 'iOS'
    
    return { browser, os }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    trackLoginAttempt({ user_type: userType, email: formData.email })

    try {
      // Get user location and browser info
      const locationInfo = await getUserLocation()
      const { browser, os } = getBrowserInfo()
      
      console.log('🌍 Location Info:', locationInfo)
      console.log('💻 Browser Info:', { browser, os })

      // Use the updated unified_login RPC function with location data
      const { data, error } = await supabase.rpc('unified_login', {
        email_param: formData.email,
        password_param: formData.password,
        user_ip: locationInfo.ip,
        user_agent: navigator.userAgent,
        device_type: 'web',
        browser: browser,
        os: os,
        location_city: locationInfo.city,
        location_state: locationInfo.state,
        location_country: locationInfo.country,
        location_latitude: locationInfo.latitude,
        location_longitude: locationInfo.longitude
      })

      if (error) {
        console.error('Login error:', error)
        trackLoginFailure({ user_type: userType, email: formData.email, error: error.message })
        toast.error('Login failed. Please try again.')
        return
      }

      if (data && data.length > 0 && data[0].success) {
        const loginResult = data[0]
        
        const userData = {
          id: loginResult.user_id,
          email: loginResult.user_email,
          role: loginResult.role, // Store role at top level
          permissions: loginResult.permissions, // Store permissions at top level
          user_metadata: {
            first_name: loginResult.user_name.split(' ')[0],
            last_name: loginResult.user_name.split(' ').slice(1).join(' '),
            full_name: loginResult.user_name,
            role: loginResult.role, // Store role in metadata too
            user_type: loginResult.user_type,
            permissions: loginResult.permissions // Store permissions in metadata too
          }
        }

        // Store in localStorage
        localStorage.setItem('user', JSON.stringify(userData))
        localStorage.setItem('auth_token', loginResult.session_token)
        
        // Also call the login function from AuthContext to update the context
        await login(userData)
        
        // Session is now automatically tracked by the unified_login function
        console.log('✅ Login successful with session tracking')
        console.log('🔍 Session Token:', loginResult.session_token)
        console.log('🔍 User Type:', loginResult.user_type)
        console.log('🔍 Role:', loginResult.role)
        
        trackLoginSuccess({ user_type: userType, email: loginResult.user_email, role: loginResult.role })
        identifyOneSignalUser(loginResult.user_id, loginResult.user_email, loginResult.user_name)
        toast.success(`Welcome back, ${loginResult.user_name}!`)
        
        // Debug logging
        console.log('🔍 Login Result:', loginResult)
        console.log('🔍 User Role:', loginResult.user_role)
        console.log('🔍 User Type:', loginResult.user_type)
        console.log('🔍 Selected User Type from URL:', userType)
        
        // Full page reload so PLuG re-initializes with the logged-in user
        if (userType === 'employee') {
          window.location.href = `/admin?user_id=${loginResult.user_id}`
        } else {
          window.location.href = `/customer?user_id=${loginResult.user_id}`
        }
      } else {
        trackLoginFailure({ user_type: userType, email: formData.email, reason: 'invalid_credentials' })
        toast.error('Invalid credentials. Please check your email and password.')
      }
    } catch (error) {
      console.error('Login error:', error)
      trackLoginFailure({ user_type: userType, email: formData.email, error: error.message })
      toast.error('An error occurred during login. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const toggleUserType = (newType) => {
    setUserType(newType)
    // Update URL to reflect the selected user type
    const newUrl = newType === 'employee' 
      ? '/auth/login?type=employee' 
      : '/auth/login?type=customer'
    navigate(newUrl, { replace: true })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header with Logo */}
      <header className="absolute top-0 left-0 right-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <Shield className="h-8 w-8 text-blue-600" />
              <span className="text-xl font-bold text-gray-900">YourInsurance</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex items-center justify-center min-h-screen py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          {/* Login Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* User Type Toggle */}
            <div className="flex justify-center mb-6">
              <div className="bg-gray-100 rounded-lg p-1 inline-flex">
                <button
                  onClick={() => toggleUserType('customer')}
                  className={`flex items-center justify-center px-8 py-3 rounded-md font-semibold text-base transition-all duration-200 min-w-[140px] ${
                    userType === 'customer'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <User className="w-5 h-5 mr-2" />
                  Customer
                </button>
                <button
                  onClick={() => toggleUserType('employee')}
                  className={`flex items-center justify-center px-8 py-3 rounded-md font-semibold text-base transition-all duration-200 min-w-[140px] ${
                    userType === 'employee'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Building2 className="w-5 h-5 mr-2" />
                  Employee
                </button>
              </div>
            </div>

            {/* Card Header */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
                  {userType === 'employee' ? (
                    <Building2 className="w-8 h-8 text-white" />
                  ) : (
                    <User className="w-8 h-8 text-white" />
                  )}
                </div>
              </div>
              <h2 className="text-3xl font-bold text-gray-900">
                {userType === 'employee' ? 'Employee Login' : 'Customer Login'}
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                {userType === 'employee' 
                  ? 'Sign in to your employee account' 
                  : 'Sign in to your customer account'
                }
              </p>
            </div>

            {/* Login Form */}
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <div className="mt-1">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                      placeholder="Enter your email"
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={formData.password}
                      onChange={handleInputChange}
                      className="appearance-none relative block w-full px-3 py-2 pr-12 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-50 rounded-r-md transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-500 hover:text-gray-700" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-500 hover:text-gray-700" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    'Sign In'
                  )}
                </button>
              </div>

              {/* Links - Only show for Customer */}
              {userType === 'customer' && (
                <div className="text-center">
                  <p className="text-sm text-gray-600">
                    Don't have an account?{' '}
                    <Link 
                      to="/auth/register" 
                      className="font-medium text-blue-600 hover:text-blue-500"
                    >
                      Register here
                    </Link>
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login